import { launchpadError } from "../../../wallet/launchpad-errors";
import { useMemo, useState } from "react";
import { formatEther, type Address } from "viem";
import { launchChain, launchRequest } from "../../../api/launchpad";
import { ArrowUpRightIcon, ChevronDownIcon, InfoIcon, LeafIcon } from "../../../components/Icons";
import { Overlay } from "../../../components/overlays/Overlay";
import { executeLaunchTx, unresolvedLaunchTx } from "../../../wallet/launchpad-transactions";
import { creatorFees, feeClaimCall, feeReviewKey, type ConfiguredFees, type FeeRole } from "../state/creator-fees";
import { useLaunchRead } from "../state/use-launch-read";
import { transactionScope, useLaunchTransaction, useLaunchWallet } from "../state/use-launch-wallet";
import { LaunchTransactionStatus } from "./LaunchTransactionStatus";
import "../styles/creator-fees.css";

export function CreatorFeesOverlay({ onClose }: { onClose: () => void }) {
  const wallet = useLaunchWallet("launchpad-fees");
  const { account, chainId } = wallet.snapshot;
  return <Overlay open title="Creator fees" eyebrow="Launchpad" variant="sheet" className="creator-fees-sheet" onClose={onClose}>
    {account ? <FeeBalances key={JSON.stringify([account, chainId, wallet.identity, wallet.revision])} account={account} wallet={wallet} /> :
      <p>Connect your wallet to view creator fees.</p>}
  </Overlay>;
}

type FeeWallet = ReturnType<typeof useLaunchWallet>;

function FeeBalances({ account, wallet }: { account: Address; wallet: FeeWallet }) {
  const [feeKind, setFeeKind] = useState<"lp" | "anti">("lp");
  const creatorScope = transactionScope(account, "creator-claim-all" + (feeKind === "anti" ? ":anti" : ""));
  const protocolScope = transactionScope(account, "protocol-claim-all" + (feeKind === "anti" ? ":anti" : ""));
  const creator = useLaunchTransaction(creatorScope);
  const protocol = useLaunchTransaction(protocolScope);
  const creatorConfirmed = creator?.state === "confirmed" ? creator : undefined;
  const protocolConfirmed = protocol?.state === "confirmed" ? protocol : undefined;
  const refreshKey = JSON.stringify([creatorConfirmed?.hash, protocolConfirmed?.hash]);
  const confirmedBlock = Math.max(creatorConfirmed?.block ?? 0, protocolConfirmed?.block ?? 0);
  return <>
    <div className="creator-fees-tabs" role="group" aria-label="Fee source"><button type="button" disabled={wallet.busy} aria-pressed={feeKind === "lp"} onClick={() => setFeeKind("lp")}>LP fees</button><button type="button" disabled={wallet.busy} aria-pressed={feeKind === "anti"} onClick={() => setFeeKind("anti")}>Anti-sniper fees</button></div>
    <FeeSection key={"creator:" + feeKind} feeKind={feeKind} account={account} role="creator" scope={creatorScope} wallet={wallet} refreshKey={refreshKey} confirmedBlock={confirmedBlock} />
    <FeeSection key={"protocol:" + feeKind} feeKind={feeKind} account={account} role="protocol" scope={protocolScope} wallet={wallet} refreshKey={refreshKey} confirmedBlock={confirmedBlock} />
  </>;
}

const FEES_PAGE_SIZE = 10;

// Display only: retain the original decimal string for review and signing.
function compactFeeAmount(value: string) {
  const [integer, fraction = ""] = value.split(".");
  const whole = integer!.replace(/^0+(?=\d)/, "");
  const precision = whole === "0" ? 6 : 4;
  const decimal = fraction.slice(0, precision).replace(/0+$/, "");
  if (whole === "0" && !decimal && /[1-9]/.test(fraction)) return "< 0.000001";
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (decimal ? "." + decimal : "");
}

function ClaimableAmounts({ total, split, feeKind }: { feeKind: "lp" | "anti"; total: ConfiguredFees["totalClaimable"]; split: ConfiguredFees["split"] }) {
  const [showZero, setShowZero] = useState(false);
  const [page, setPage] = useState(0);
  if (!total) return <p>Claimable fee data is missing.</p>;
  const zeroCount = total.simulated ? total.tokens.filter(token => /^0+$/.test(token.raw)).length : 0;
  const tokens = total.tokens.filter(token => !total.simulated || showZero || !/^0+$/.test(token.raw));
  const pageCount = Math.max(1, Math.ceil(tokens.length / FEES_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const start = currentPage * FEES_PAGE_SIZE;
  return <>
    <div className="creator-fees-hero">
      <LeafIcon className="creator-fees-leaf" />
      <span className="creator-fees-caption">{feeKind === "anti" ? "Accrued surcharge · HYPE" : "Estimated claimable · HYPE"}</span>
      <div className="creator-fees-native"><strong>{total.simulated ? compactFeeAmount(total.HYPE.formatted) : "Unknown"}</strong><span>HYPE</span></div>
      {total.simulated && <>
        <span className="creator-fees-caption">+ {total.tokens.length - zeroCount} other assets with fees</span>
        <details className="creator-fees-native-detail"><summary>Exact estimate</summary><p>{total.HYPE.formatted} HYPE</p></details>
      </>}
    </div>
    <div className="creator-fees-split"><span><strong>{split.creatorPct}%</strong> creator · <strong>{split.protocolPct}%</strong> protocol</span><span>Elysium</span></div>
    <p className="creator-fees-note"><InfoIcon /><span>{feeKind === "anti" ? "Accrued surcharge fees recorded by the hook. Actual claimable amounts may change." : total.simulated ? "Estimated claimable fees from the latest simulation. Actual amounts may change." :
      "A reliable fee simulation is unavailable. The amount you may receive is unknown."}</span></p>
    <div className="creator-fees-list-heading"><h3>Token fees</h3>
      {zeroCount > 0 && <button type="button" className="creator-fees-zero" aria-pressed={showZero}
        onClick={() => { setShowZero(value => !value); setPage(0); }}>{showZero ? "Hide" : "Show"} {zeroCount} zero balances</button>}
    </div>
    <div className="creator-fees-list">
      {tokens.slice(start, start + FEES_PAGE_SIZE).map(token => <details className="creator-fees-token" key={token.address.toLowerCase()}>
        <summary>
          <span className="creator-fees-identity"><span className="creator-fees-avatar" aria-hidden="true">{token.symbol.slice(0, 2)}</span>
            <span className="creator-fees-token-name"><strong>{token.symbol}</strong><span className="creator-fees-address">{token.address.slice(0, 6)}…{token.address.slice(-4)}</span></span>
          </span>
          <span className="creator-fees-amount"><strong>{total.simulated ? compactFeeAmount(token.formatted) : "Unknown"}</strong><small>{total.simulated ? /^0+$/.test(token.raw) ? "No fees" : "Estimated" : "Unavailable"}</small></span>
          <ChevronDownIcon className="creator-fees-expand" />
        </summary>
        <dl className="creator-fees-detail"><div><dt>Exact estimate</dt><dd>{total.simulated ? token.formatted : "Unknown"} {token.symbol}</dd></div>
          <div><dt>Contract</dt><dd>{token.address}</dd></div></dl>
      </details>)}
      {tokens.length === 0 && <p className="creator-fees-empty">{zeroCount ? "All token balances are zero." : "No token fees to display."}</p>}
    </div>
    <nav className="creator-fees-pagination" aria-label="Token fee pages">
      <span role="status">{tokens.length ? start + 1 : 0}–{Math.min(start + FEES_PAGE_SIZE, tokens.length)} of {tokens.length} assets</span>
      {pageCount > 1 && <div><button type="button" aria-label="Previous token fees" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}><ChevronDownIcon /></button>
        <span>{currentPage + 1} / {pageCount}</span>
        <button type="button" aria-label="Next token fees" disabled={currentPage === pageCount - 1} onClick={() => setPage(currentPage + 1)}><ChevronDownIcon /></button></div>}
    </nav>
  </>;
}

function FeeSection({ account, role, scope, wallet, refreshKey, confirmedBlock, feeKind }: {
  feeKind: "lp" | "anti"; account: Address; role: FeeRole; scope: string; wallet: FeeWallet; refreshKey: string; confirmedBlock: number;
}) {
  const record = useLaunchTransaction(scope);
  const [review, setReview] = useState<ConfiguredFees>();
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const label = role === "creator" ? "Creator" : "Protocol";
  const path = (role === "creator" ? "/creators/" + account + "/fees" : "/protocol/fees") +
    "?" + new URLSearchParams({ recipient: account });
  const schema = useMemo(() => creatorFees(account, role, feeKind), [account, role, feeKind]);
  const read = useLaunchRead(path, schema, "trade", undefined, refreshKey + ":" + refresh);
  const response = read?.data;
  const data = response?.data;
  const status = record && <LaunchTransactionStatus scope={scope} />;

  function confirm(reviewed: ConfiguredFees) {
    setError("");
    void wallet.run(async (sender, provider, check) => {
      const fresh = await launchRequest(path, schema);
      check();
      if (!fresh.data.configured || fresh.meta.indexedBlock < confirmedBlock) {
        setReview(undefined);
        setRefresh(value => value + 1);
        setError("Claim preparation is unavailable. Wait for updated fee data.");
        return;
      }
      if (!fresh.data.claimAllTransaction || !fresh.data.totalClaimable ||
          fresh.data.account.toLowerCase() !== sender.toLowerCase()) {
        setReview(undefined);
        setRefresh(value => value + 1);
        setError(role === "protocol" && fresh.data.account.toLowerCase() !== sender.toLowerCase()
          ? "The connected wallet no longer matches the protocol treasury."
          : "Claim details are no longer available. Wait for updated fee data.");
        return;
      }
      const call = feeClaimCall(fresh.data, sender);
      if (feeReviewKey(fresh.data) !== feeReviewKey(reviewed)) {
        setReview(fresh.data);
        setError("Fees or transaction details changed. Review the updated claim before confirming again.");
        return;
      }
      setReview(undefined);
      await executeLaunchTx(scope, sender, provider, call, check);
    }).catch((cause: unknown) => {
      setError(launchpadError(cause));
    });
  }

  function renderFees() {
    if (!response || !data) return read?.error
      ? <p role="alert">{role === "protocol" ? "Protocol treasury eligibility could not be verified from the fee endpoint." :
        "Creator fees could not be loaded or verified."}</p>
      : <p role="status">{confirmedBlock || refresh ? "Refreshing" : "Loading"} {role} fees…</p>;
    if (!data.configured) return <p>{label} fees are not configured.</p>;
    if (role === "protocol" && data.account.toLowerCase() !== account.toLowerCase()) return null;
    const waiting = response.meta.indexedBlock < confirmedBlock;
    const claimBlocked = wallet.busy || wallet.snapshot.chainId !== launchChain || Boolean(read.error) ||
      waiting || !data.claimAllTransaction || !data.totalClaimable || Boolean(unresolvedLaunchTx(record));
    const displayed = review ?? data;
    return <>
      {role === "protocol" && <h3>Protocol fees</h3>}
      {read.error && <p role="alert">{label} fee refresh failed.</p>}
      {waiting && <p role="status">Claim confirmed. Waiting for updated fee data…</p>}
      {Boolean(read.offline || response.meta.indexerStatus !== "ready") && <p className="creator-fees-delayed" role="status"><InfoIcon />{label} fee data may be delayed.</p>}
      <ClaimableAmounts feeKind={feeKind} total={displayed.totalClaimable} split={data.split} />
      {wallet.snapshot.chainId !== launchChain && <p>Switch to Elysium to claim.</p>}
      {!data.claimAllTransaction && <p>No claim-all transaction is currently available.</p>}
      {review ? <div className="creator-fees-actions">
        <h3>Review your claim</h3>
        <p className="creator-fees-recipient">Recipient <span>{account}</span></p>
        {review.claimAllTransaction && BigInt(review.claimAllTransaction.value) > 0n &&
          <p>Transaction value {formatEther(BigInt(review.claimAllTransaction.value))} HYPE</p>}
        <button type="button" className="primary" disabled={claimBlocked}
          onClick={() => confirm(review)}>Confirm {role} {feeKind === "lp" ? "LP" : "anti-sniper"} claim</button>
        <button type="button" className="text-button" disabled={wallet.busy} onClick={() => { setReview(undefined); setError(""); }}>Cancel review</button>
      </div> : data.claimAllTransaction && <div className="creator-fees-actions"><button type="button" className="primary" disabled={claimBlocked}
        onClick={() => { setError(""); setReview(data); }}>Claim all {feeKind === "lp" ? "LP" : "anti-sniper"} fees<ArrowUpRightIcon /></button>
        <p className="creator-fees-action-note">Review before confirming · Claims all eligible assets across all pages</p></div>}
    </>;
  }

  return <section className="creator-fees-section" aria-label={label + " fees"}>{status}{renderFees()}{error && <p role="alert">{error}</p>}</section>;
}
